import React, {ReactElement} from "react"
import TitleBar from "../../components/TitleBar/TitleBar"
import {MainSectionTable} from "../../containers/MainSection/MainSection"
import {CheckDeposits} from "../../components/CheckDeposits/CheckDeposits"
import {useAccessToken, useIsBankUser, useIsOrgAdmin, useIsUnitUser, useUserClaimsData} from "../../services/auth"
import {hasPermission} from "../../services/permission"
import {CheckDepositStatus} from "../../resources/checkDeposits"
import {TabsMeta, TitleBarTabs} from "../../components/TitleBarTabs/TitleBarTabs"
import {AssigneeType, AssignmentResourceType, AssignmentStage} from "../../resources/assignments"
import {startCase} from "lodash"
import {useQueryState} from "use-location-state"
import {AssignmentsHub, AssignmentsHubColumns} from "../AssignmentsHub/AssignmentsHub"
import {AssignmentsAmountIndicator} from "../../components/AssignmentAmountIndicator/AssignmentsAmountIndicator"
import {CheckDepositColumns} from "../../components/CheckDeposits/CheckDeposit.types"

export function CheckDepositsPage(): ReactElement {
	const isUnitUser = useIsUnitUser()
	const isBankUser = useIsBankUser()
	const isOrgAdmin = useIsOrgAdmin()
	const accessToken = useAccessToken()
	const claims = useUserClaimsData()
	const liClassName = "assignments-tab-li" as const
	const commonTabProps = {liClassName}
	const assigneeType = startCase(claims.userType) as AssigneeType

	enum CheckDepositsTabs {
		All = "All",
		Assignments = "Assignments",
	}

	const [currentTab, setCurrentTab] = useQueryState<CheckDepositsTabs>("tab", CheckDepositsTabs.All)

	const tabs: TabsMeta<CheckDepositsTabs>[] = [
		{label: "All", key: CheckDepositsTabs.All},
		{
			label: "Assignments",
			key: CheckDepositsTabs.Assignments,
			suffixElement: (
				<AssignmentsAmountIndicator
					findAssignmentsArgs={{
						accessToken,
						assignees: [assigneeType],
						stages: [AssignmentStage.Pending],
						limit: 0,
						resourceTypes: [AssignmentResourceType.CheckDeposit],
					}}
					isActive={currentTab == CheckDepositsTabs.Assignments}
					tooltip={
						<>
							Pending assignment
							<br /> for {assigneeType} user
						</>
					}
				/>
			),
		},
	].map((tab) => ({...tab, ...commonTabProps}))

	const checkDepositsProps = {
		limit: 40,
		fullHeight: true,
		stickyAction: true,
		includedColumns: [
			CheckDepositColumns.id,
			...(isUnitUser || isBankUser ? [CheckDepositColumns.org] : []),
			CheckDepositColumns.vendor,
			CheckDepositColumns.customer,
			CheckDepositColumns.accountId,
			CheckDepositColumns.status,
			CheckDepositColumns.depositDate,
			CheckDepositColumns.expectedCompletionDate,
			CheckDepositColumns.amount,
			...(hasPermission("checkDeposit.approve", "update") || isOrgAdmin ? [CheckDepositColumns.actions] : []),
		],
		initialStatusFilter: isUnitUser ? [CheckDepositStatus.PendingReview] : [],
		disableDrawer: isUnitUser ? true : false,
	}

	const assignmentHubIncludedColumns = [
		AssignmentsHubColumns.isOwner,
		AssignmentsHubColumns.resourceType,
		AssignmentsHubColumns.resourceId,
		...(isUnitUser || isBankUser ? [AssignmentsHubColumns.org] : []),
		...(isUnitUser ? [AssignmentsHubColumns.bank] : []),
		AssignmentsHubColumns.customer,
		AssignmentsHubColumns.assignmentId,
		AssignmentsHubColumns.assignmentType,
		AssignmentsHubColumns.assignee,
		AssignmentsHubColumns.assignmentStatus,
		AssignmentsHubColumns.createdAt,
	]

	return (
		<>
			<TitleBar title="Check Deposits" noBorder />
			<TitleBarTabs
				currentTab={currentTab}
				setCurrentTab={setCurrentTab}
				tabs={tabs}
				navClassName="assignments-tab-nav"
			/>
			<MainSectionTable>
				{currentTab == CheckDepositsTabs.All && <CheckDeposits {...checkDepositsProps} showTabsOnHeader />}
				{currentTab == CheckDepositsTabs.Assignments && (
					<AssignmentsHub
						token={accessToken}
						includedColumns={assignmentHubIncludedColumns}
						enableResourceTypeFilter={false}
						resourceTypes={[AssignmentResourceType.CheckDeposit]}
						enableAssignmentTypeFilter={false}
						enableOrgFilter={isUnitUser || isBankUser}
						enableBankFilter={isUnitUser}
						fullHeight
						enableAssigneeFilter
						enableAssignmentStageFilter
						enableAssignmentIsEscalatedFilter
					/>
				)}
			</MainSectionTable>
		</>
	)
}
